import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { authentication } from "@microsoft/teams-js";
import { Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";
import { API_BASE } from "../api/client";

export interface Agent {
  id: string;
  email: string;
  displayName: string;
  role: string;
  avatarUrl?: string;
  jobTitle?: string;
}

interface AuthState {
  token: string;
  agent: Agent;
  workspaceId: string | null;
  workspaces: { workspaceId: string; role: string }[];
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within TeamsAuthProvider");
  return ctx;
}

const useStyles = makeStyles({
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    gap: tokens.spacingVerticalM,
  },
});

export function TeamsAuthProvider({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    async function authenticate() {
      try {
        const teamsToken = await authentication.getAuthToken();

        const urlParams = new URLSearchParams(window.location.search);
        const workspaceId = urlParams.get("workspaceId") ?? undefined;

        const resp = await fetch(`${API_BASE}/auth/teams-sso`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: teamsToken, workspaceId }),
        });

        const json = await resp.json() as {
          data?: {
            token: string;
            agent: Agent;
            workspaceId: string | null;
            workspaces: { workspaceId: string; role: string }[];
          };
          message?: string;
        };

        if (!resp.ok || !json.data) {
          throw new Error(json.message ?? "Authentication failed");
        }

        setAuth({
          token: json.data.token,
          agent: json.data.agent,
          workspaceId: json.data.workspaceId,
          workspaces: json.data.workspaces,
        });
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : "Authentication failed");
      } finally {
        setLoading(false);
      }
    }

    authenticate();
  }, []);

  if (loading) {
    return (
      <div className={styles.center}>
        <Spinner size="large" label="Signing you in..." />
      </div>
    );
  }

  if (authError || !auth) {
    return (
      <div className={styles.center}>
        <Text size={400} weight="semibold">Unable to sign in</Text>
        <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
          {authError ?? "Please contact your workspace administrator."}
        </Text>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}
